import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../shared/services/api/api.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex items-center justify-center min-h-screen bg-gray-50">
      <div class="text-center max-w-md w-full px-4">
        @if (loading) {
          <div class="space-y-4">
            <div
              class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"
            ></div>
            <p class="text-gray-600">Verifying your email address...</p>
          </div>
        }

        @if (!loading && success) {
          <div class="space-y-4">
            <div
              class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4"
            >
              <svg
                class="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900">Email Verified!</h2>
            <p class="text-gray-600">
              Your email address has been successfully verified. You can now log in to your account.
            </p>
            <button
              (click)="goToLogin()"
              class="mt-6 w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-md shadow-md transition duration-200"
            >
              Go to Login
            </button>
          </div>
        }

        @if (!loading && !success && error) {
          <div class="space-y-4">
            <div
              class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4"
            >
              <svg
                class="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 class="text-2xl font-bold text-gray-900">Verification Failed</h2>
            <p class="text-gray-600">{{ error }}</p>
            <div class="flex gap-4 justify-center mt-6">
              <button
                (click)="goToLogin()"
                class="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md shadow-md transition duration-200"
              >
                Go to Login
              </button>
              <button
                (click)="resendVerification()"
                [disabled]="resendingVerification"
                class="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md shadow-md transition duration-200"
              >
                @if (resendingVerification) {
                  <span>Sending...</span>
                } @else {
                  <span>Resend Verification Email</span>
                }
              </button>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  loading = true;
  success = false;
  error: string | null = null;
  token: string | null = null;
  email: string | null = null;
  shortCode: string | null = null;
  resendingVerification = false;
  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private toastService: ToastService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    // Only work in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Get route params (short_code) and query params (token, email) in a single subscription
    this.subscriptions.add(
      combineLatest([this.route.params, this.route.queryParams]).subscribe(
        ([routeParams, queryParams]) => {
          this.shortCode = routeParams['short_code'] || null;
          this.token = queryParams['token'] || null;
          this.email = queryParams['email'] || null;

          // If email is not in query params, try to extract from token (if it's a JWT)
          if (!this.email && this.token) {
            this.email = this.extractEmailFromToken(this.token);
          }

          const verificationKey = this.token;
          if(this.shortCode){
            this.verifyEmailByShortCode(this.shortCode);
          } else if (verificationKey) {
            this.verifyEmail(verificationKey);
          } else {
            this.loading = false;
            this.error =
              'Verification token is missing. Please check your email and try again.';
          }
        },
      ),
    );
  }

  /**
   * Verify email with token
   */
  private verifyEmail(token: string): void {
    this.loading = true;
    this.error = null;
    this.success = false;

    // Call backend API to verify email - POST request with token in query params
    // Note: Using POST with query params as backend expects token in query
    this.apiService
      .post('/auth/verify-email?token=' + encodeURIComponent(token), {}, false)
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          this.success = true;
          this.toastService.success('Email verified successfully!', 'Success');
        },
        error: (err: any) => {
          this.loading = false;
          this.success = false;

          // Extract error message
          const errorMessage =
            err?.message ||
            err?.error?.detail ||
            err?.error?.error ||
            'Email verification failed. The link may have expired or is invalid.';
          this.error = errorMessage;

          // Show error toast
          this.toastService.error(errorMessage, 'Verification Failed');
        },
      });
  }

  /**
   * Verify email by short code
   */
  private verifyEmailByShortCode(shortCode: string): void {
    this.loading = true;
    this.error = null;
    this.success = false;
    this.apiService.get(`/auth/v/${shortCode}`, {}, false).subscribe({
      next: (_response: any) => {
        this.loading = false;
        this.success = true;
        this.toastService.success('Email verified successfully!', 'Success');
      },
      error: (err: any) => {
        this.loading = false;
        this.success = false;
        const errorMessage =
          err?.message ||
          err?.error?.detail ||
          (typeof err?.error === 'string' ? err.error : err?.error?.error) ||
          'Email verification failed. The link may have expired or is invalid.';
        this.error = errorMessage;
        this.toastService.error(errorMessage, 'Verification Failed');
      },
    });
  }

  /**
   * Navigate to login page
   */
  goToLogin(): void {
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  /**
   * Extract email from JWT token if possible
   */
  private extractEmailFromToken(token: string): string | null {
    try {
      // Try to decode JWT token (format: header.payload.signature)
      const parts = token.split('.');
      if (parts.length === 3 && parts[1]) {
        // Decode the payload (second part)
        const payload = JSON.parse(atob(parts[1]));
        // Check for email in common JWT fields
        return payload.email || payload.sub || payload.user_email || null;
      }
    } catch (error) {
      // Token is not a JWT or decoding failed
      console.warn('Could not extract email from token:', error);
    }
    return null;
  }

  /**
   * Resend verification email
   */
  resendVerification(): void {
    // Check if we have email
    if (!this.email) {
      // If no email available, redirect to login with message
      this.toastService.error(
        'Email address is required. Please go to login page and use the resend verification option.',
        'Email Required',
      );
      setTimeout(() => {
        this.router.navigate(['/login'], { replaceUrl: true });
      }, 2000);
      return;
    }

    this.resendingVerification = true;
    this.subscriptions.add(
      this.authService.resendVerificationEmail(this.email).subscribe({
        next: (result) => {
          this.resendingVerification = false;
          if (result.success) {
            // Success toast is already shown by AuthService
            // Optionally, we could show additional message
            this.toastService.success(
              'Please check your inbox for the verification email.',
              'Email Sent',
            );
          }
        },
        error: (err) => {
          this.resendingVerification = false;
          // Error toast is already shown by AuthService
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
